import { useEffect, useMemo, useState } from "react";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import {
  Calendar,
  ChevronDown,
  ChevronsLeft,
  ChevronsRight,
  LogOut,
  Menu,
  X,
} from "lucide-react";
import MahindraLogo from "@/components/MahindraLogo";
import LoginSidebarShell from "@/components/layout/LoginSidebarShell";
import { useRole } from "@/store/role";
import { groupedNavForUser, isStudentUser, navItemLabel, navItemSublabel } from "@/config/navigation";
import { clearAuthSession } from "@/lib/auth";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

const SIDEBAR_KEY = "mu_sidebar_collapsed";

function useSidebarCollapse() {
  const [collapsed, setCollapsed] = useState(() => {
    try {
      return localStorage.getItem(SIDEBAR_KEY) === "1";
    } catch {
      return false;
    }
  });
  useEffect(() => {
    try { localStorage.setItem(SIDEBAR_KEY, collapsed ? "1" : "0"); } catch { /* ignore */ }
  }, [collapsed]);
  return [collapsed, setCollapsed];
}

function roleLabel(u) {
  if (!u) return "";
  if (isStudentUser(u)) return "Student";
  return "Staff";
}

function profileInitials(name) {
  const parts = String(name ?? "")
    .trim()
    .split(/\s+/)
    .filter(Boolean);
  if (!parts.length) return "MU";
  return parts.map((p) => p[0]).join("").slice(0, 2).toUpperCase();
}

function headerProfileLines(user, department) {
  const isStudent = isStudentUser(user);
  const name = user?.name?.trim() || (isStudent ? "Student" : "Staff");
  const designation = user?.title?.trim() || "";
  const dept = department?.name?.trim() || user?.department_name?.trim() || "";
  const htno = user?.student_id?.trim() || "";
  const staffId = user?.staff_id?.trim() || "";

  if (isStudent) {
    return {
      primary: name,
      secondary: htno ? `HTNO · ${htno}` : roleLabel(user),
      tertiary: user?.email?.trim() || user?.mobile?.trim() || "",
    };
  }

  return {
    primary: name,
    secondary: designation || dept || (staffId ? `ID · ${staffId}` : roleLabel(user)),
    tertiary: designation && dept ? dept : user?.email?.trim() || "",
  };
}

function isNavItemActive(pathname, to, hash = "") {
  const [path, itemHash = ""] = to.split("#");
  const currentHash = hash || "";

  if (path === "/hr/dashboard" || path === "/hostels/dashboard") {
    return pathname === path && !itemHash;
  }

  if (pathname !== path && !pathname.startsWith(`${path}/`)) {
    return false;
  }

  if (itemHash) {
    return currentHash === `#${itemHash}` || currentHash === itemHash;
  }

  return pathname === path || pathname.startsWith(`${path}/`);
}

function sidebarLinkClass({ collapsed, isActive }) {
  return `group flex items-center w-full ${collapsed ? "justify-center px-2 py-3" : "gap-3 mx-1 px-4 py-3"} rounded-2xl text-sm font-semibold transition-all ${
    isActive
      ? "nav-active text-[#fff2f6]"
      : "text-[#d3b7c0] hover:text-[#fff2f6] hover:bg-white/5"
  }`;
}

function SidebarNavLink({ item, collapsed, pathname, hash }) {
  const Icon = item.icon;
  const label = navItemLabel(item);
  const isActive = isNavItemActive(pathname, item.to, hash);

  return (
    <NavLink
      to={item.to}
      data-testid={`sidebar-nav-${item.key}`}
      title={collapsed ? label : undefined}
      className={sidebarLinkClass({ collapsed, isActive })}
      end={item.to === "/hr/dashboard" || item.to === "/hostels/dashboard"}
    >
      <Icon className="nav-icon h-[18px] w-[18px] shrink-0" strokeWidth={2.2} />
      {!collapsed && <span className="truncate">{label}</span>}
    </NavLink>
  );
}

function SidebarNavDropdown({ entry, collapsed, pathname, hash }) {
  const navigate = useNavigate();
  const isActive = entry.items.some((item) => isNavItemActive(pathname, item.to, hash));
  const [open, setOpen] = useState(isActive);
  const Icon = entry.icon;

  useEffect(() => {
    if (isActive) setOpen(true);
  }, [isActive]);

  if (collapsed) {
    return (
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button
            type="button"
            className={sidebarLinkClass({ collapsed, isActive })}
            data-testid={`sidebar-nav-dropdown-${entry.label.replace(/\s+/g, "-").toLowerCase()}`}
            title={entry.label}
          >
            <Icon className="nav-icon h-[18px] w-[18px] shrink-0" strokeWidth={2.2} />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent side="right" align="start" className="min-w-[200px]">
          <DropdownMenuLabel className="text-xs uppercase tracking-wider text-mahindra-steel">
            {entry.label}
          </DropdownMenuLabel>
          <DropdownMenuSeparator />
          {entry.items.map((item) => (
            <DropdownMenuItem
              key={item.key}
              onClick={() => navigate(item.to)}
              className="cursor-pointer"
              data-testid={`sidebar-nav-${item.key}`}
            >
              {navItemSublabel(entry, item)}
            </DropdownMenuItem>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>
    );
  }

  return (
    <Collapsible open={open} onOpenChange={setOpen}>
      <CollapsibleTrigger
        className={`${sidebarLinkClass({ collapsed, isActive })} justify-between`}
        data-testid={`sidebar-nav-dropdown-${entry.label.replace(/\s+/g, "-").toLowerCase()}`}
      >
        <span className="flex items-center gap-3 min-w-0">
          <Icon className="nav-icon h-[18px] w-[18px] shrink-0" strokeWidth={2.2} />
          <span className="truncate">{entry.label}</span>
        </span>
        <ChevronDown
          className={`h-4 w-4 shrink-0 transition-transform ${open ? "rotate-180" : ""}`}
        />
      </CollapsibleTrigger>
      <CollapsibleContent className="mt-1 space-y-1 pl-3">
        {entry.items.map((item) => {
          const subActive = isNavItemActive(pathname, item.to, hash);
          const sublabel = navItemSublabel(entry, item);
          return (
            <NavLink
              key={item.key}
              to={item.to}
              data-testid={`sidebar-nav-${item.key}`}
              title={entry.dropdownMode === "module" ? item.label : undefined}
              className={`block mx-1 px-4 py-2.5 rounded-xl text-sm font-semibold transition-all ${
                subActive
                  ? "nav-active text-[#fff2f6]"
                  : "text-[#d3b7c0] hover:text-[#fff2f6] hover:bg-white/5"
              }`}
            >
              {sublabel}
            </NavLink>
          );
        })}
      </CollapsibleContent>
    </Collapsible>
  );
}

function Sidebar({ collapsed, onToggle, mobileOpen, onCloseMobile }) {
  const { currentUser, permissions } = useRole();
  const { pathname, hash } = useLocation();
  const navGroups = useMemo(
    () => groupedNavForUser(currentUser, permissions),
    [currentUser, permissions],
  );
  const [isDesktop, setIsDesktop] = useState(() =>
    typeof window !== "undefined" ? window.matchMedia("(min-width: 768px)").matches : true,
  );

  useEffect(() => {
    const mq = window.matchMedia("(min-width: 768px)");
    const update = () => setIsDesktop(mq.matches);
    update();
    mq.addEventListener("change", update);
    return () => mq.removeEventListener("change", update);
  }, []);

  // Drawer is always expanded on mobile; desktop respects collapse.
  const effectiveCollapsed = isDesktop ? collapsed : false;

  return (
    <aside
      className={`fixed left-0 top-0 h-screen z-40 p-3 transition-transform duration-200 md:transition-[width] ${
        effectiveCollapsed ? "md:w-[88px] md:p-2" : "md:w-[256px] md:p-3"
      } w-[min(100vw-3rem,256px)] ${
        mobileOpen ? "translate-x-0" : "-translate-x-full max-md:pointer-events-none"
      } md:translate-x-0 md:pointer-events-auto`}
      data-testid="sidebar"
      data-collapsed={effectiveCollapsed ? "true" : "false"}
    >
      <LoginSidebarShell className="h-full">
        <div
          className={`shrink-0 flex items-start justify-between gap-2 ${
            effectiveCollapsed ? "px-2 pt-4 pb-2" : "px-4 pt-5 pb-3"
          }`}
        >
          <div className={`min-w-0 ${effectiveCollapsed ? "mx-auto" : "flex-1"}`}>
            <MahindraLogo
              compact={effectiveCollapsed}
              elevated
              className={effectiveCollapsed ? "mx-auto" : "w-full"}
            />
          </div>
          <button
            type="button"
            onClick={onCloseMobile}
            className="md:hidden shrink-0 rounded-xl p-2 text-[#d3b7c0] hover:bg-white/5 hover:text-[#fff2f6]"
            aria-label="Close navigation"
            data-testid="sidebar-close-mobile"
          >
            <X className="h-5 w-5" strokeWidth={2.2} />
          </button>
        </div>

        {!effectiveCollapsed && (
          <div className="px-6 pb-2">
            <p className="text-[10px] uppercase tracking-[0.22em] text-[#ff6a86] font-bold">Navigation</p>
          </div>
        )}
        {effectiveCollapsed && <div className="pt-2" />}

        <nav
          className={`flex-1 min-h-0 ${effectiveCollapsed ? "px-2" : "px-3"} space-y-1.5 no-scrollbar overflow-y-auto`}
          onClick={(e) => {
            if (e.target.closest("a")) onCloseMobile?.();
          }}
        >
          {navGroups.map((group) => (
            <div key={group.section} className="mb-3">
              {!effectiveCollapsed && navGroups.length > 1 && (
                <p className="px-4 pb-1.5 pt-2 text-[9px] uppercase tracking-[0.2em] text-[#d3b7c0]/80 font-bold">
                  {group.section}
                </p>
              )}
              {group.entries.map((entry) => {
                if (entry.type === "link") {
                  return (
                    <SidebarNavLink
                      key={entry.item.key}
                      item={entry.item}
                      collapsed={effectiveCollapsed}
                      pathname={pathname}
                      hash={hash}
                    />
                  );
                }
                return (
                  <SidebarNavDropdown
                    key={`dropdown-${entry.label}`}
                    entry={entry}
                    collapsed={effectiveCollapsed}
                    pathname={pathname}
                    hash={hash}
                  />
                );
              })}
            </div>
          ))}
        </nav>

        <div className={`${effectiveCollapsed ? "px-2" : "px-3"} pt-2 pb-4 hidden md:block`}>
          <button
            onClick={onToggle}
            aria-label={collapsed ? "Expand sidebar" : "Collapse sidebar"}
            data-testid="sidebar-toggle"
            className={`w-full flex items-center ${effectiveCollapsed ? "justify-center px-2" : "gap-3 px-4"} py-2.5 rounded-2xl text-xs font-bold uppercase tracking-wider text-[#d3b7c0] hover:text-[#fff2f6] hover:bg-white/5 transition-colors`}
            title={collapsed ? "Expand sidebar" : "Collapse sidebar"}
          >
            {effectiveCollapsed
              ? <ChevronsRight className="h-[18px] w-[18px]" strokeWidth={2.2} />
              : <>
                  <ChevronsLeft className="h-[18px] w-[18px]" strokeWidth={2.2} />
                  <span>Collapse</span>
                </>}
          </button>
        </div>
      </LoginSidebarShell>
    </aside>
  );
}

function HeaderProfileMenu() {
  const navigate = useNavigate();
  const { currentUser, currentDepartment } = useRole();

  if (!currentUser) return null;

  const lines = headerProfileLines(currentUser, currentDepartment);

  const signOut = () => {
    clearAuthSession();
    navigate("/login", { replace: true });
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          className="flex items-center gap-2.5 pl-1.5 pr-3 py-1.5 rounded-full bg-white border border-gray-100 shadow-sm hover:shadow transition-shadow max-w-[240px]"
          data-testid="header-profile-trigger"
        >
          <Avatar className="h-9 w-9 ring-2 ring-mahindra-crimson/20 shrink-0">
            <AvatarImage src={currentUser.avatar_url} alt={lines.primary} />
            <AvatarFallback className="bg-mahindra-crimson text-white text-xs font-bold">
              {profileInitials(lines.primary)}
            </AvatarFallback>
          </Avatar>
          <div className="min-w-0 text-left hidden sm:block">
            <div className="text-sm font-semibold text-mahindra-ink truncate leading-tight">
              {lines.primary}
            </div>
            {lines.secondary ? (
              <div className="text-[10px] text-mahindra-steel truncate leading-tight">{lines.secondary}</div>
            ) : null}
            {lines.tertiary ? (
              <div className="text-[10px] text-gray-500 truncate leading-tight">{lines.tertiary}</div>
            ) : null}
          </div>
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-72" data-testid="header-profile-menu">
        <DropdownMenuLabel className="font-normal">
          <div className="flex items-center gap-3 py-1">
            <Avatar className="h-10 w-10">
              <AvatarImage src={currentUser.avatar_url} alt={lines.primary} />
              <AvatarFallback className="bg-mahindra-crimson text-white text-xs font-bold">
                {profileInitials(lines.primary)}
              </AvatarFallback>
            </Avatar>
            <div className="min-w-0">
              <p className="text-sm font-semibold text-mahindra-ink truncate">{lines.primary}</p>
              {lines.secondary ? (
                <p className="text-[11px] text-mahindra-steel truncate">{lines.secondary}</p>
              ) : null}
              {lines.tertiary ? (
                <p className="text-[11px] text-gray-500 truncate">{lines.tertiary}</p>
              ) : null}
            </div>
          </div>
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        <DropdownMenuItem
          onClick={signOut}
          className="cursor-pointer text-mahindra-crimson focus:text-mahindra-crimson"
          data-testid="logout-button"
        >
          <LogOut className="h-4 w-4 mr-2" />
          Sign out
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function Header({ onOpenMobile }) {
  const today = new Date();
  const dateStr = today.toLocaleDateString("en-US", { weekday: "short", day: "2-digit", month: "short", year: "numeric" });
  const dateStrShort = today.toLocaleDateString("en-US", { day: "2-digit", month: "short" });

  return (
    <header
      className="h-16 sm:h-20 px-4 sm:px-6 md:px-8 flex items-center justify-between sticky top-0 bg-mahindra-lavender/80 backdrop-blur-md z-20 gap-3"
      data-testid="app-header"
    >
      <div className="flex items-center gap-2 sm:gap-3 min-w-0">
        <button
          type="button"
          onClick={onOpenMobile}
          className="md:hidden shrink-0 inline-flex h-10 w-10 items-center justify-center rounded-full bg-white border border-gray-100 shadow-sm text-mahindra-ink"
          aria-label="Open navigation"
          data-testid="sidebar-open-mobile"
        >
          <Menu className="h-5 w-5" strokeWidth={2.2} />
        </button>
        <div className="flex items-center gap-2 px-3 sm:px-4 py-2 bg-white rounded-full shadow-sm border border-gray-100 min-w-0">
          <Calendar className="h-3.5 w-3.5 text-mahindra-steel shrink-0" />
          <span className="text-[11px] font-bold text-mahindra-ink tracking-wide truncate" data-testid="header-date-pill">
            <span className="sm:hidden">{dateStrShort}</span>
            <span className="hidden sm:inline">{dateStr}</span>
          </span>
        </div>
      </div>
      <div className="flex items-center gap-3 shrink-0">
        <HeaderProfileMenu />
      </div>
    </header>
  );
}

export default function AppShell({ children }) {
  const [collapsed, setCollapsed] = useSidebarCollapse();
  const [mobileOpen, setMobileOpen] = useState(false);
  const { pathname } = useLocation();

  useEffect(() => {
    setMobileOpen(false);
  }, [pathname]);

  useEffect(() => {
    if (!mobileOpen) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") setMobileOpen(false);
    };
    document.addEventListener("keydown", onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prev;
    };
  }, [mobileOpen]);

  return (
    <div className="min-h-screen bg-mahindra-lavender">
      {mobileOpen ? (
        <button
          type="button"
          className="fixed inset-0 z-30 bg-black/40 md:hidden"
          aria-label="Close navigation overlay"
          data-testid="sidebar-overlay"
          onClick={() => setMobileOpen(false)}
        />
      ) : null}
      <Sidebar
        collapsed={collapsed}
        onToggle={() => setCollapsed((c) => !c)}
        mobileOpen={mobileOpen}
        onCloseMobile={() => setMobileOpen(false)}
      />
      <div
        className={`min-h-screen flex flex-col transition-[padding-left] duration-200 pl-0 ${
          collapsed ? "md:pl-[88px]" : "md:pl-[256px]"
        }`}
      >
        <Header onOpenMobile={() => setMobileOpen(true)} />
        <main className="flex-1 px-4 sm:px-6 md:px-8 pb-10 sm:pb-12 pt-2 animate-fade-up min-w-0">
          {children}
        </main>
      </div>
    </div>
  );
}
